"use client";

import {isToday, isYesterday, subMonths, subWeeks, format} from "date-fns";
import {memo, useMemo} from "react";
import {useParams} from "next/navigation";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  useSidebar,
} from "@/components/ui/sidebar";
import type {Chat} from "@/lib/db/schema";
import {LoaderIcon, MessageIcon} from "./icons";
import {ChatItem} from "./sidebar-history-item";
import {ClientSessionData} from "@/app/auth/session-types";
import {useChatHistory} from "@/hooks/use-api-endpoint";
import {useStreamingChatId} from "./data-stream-provider";
import {useTranslation} from "react-i18next";
import {LocalizationKeys} from "@/lib/all-localization-keys";
import {MessageStatus} from "@/lib/types";

type ChatWithMessageStatus = Chat & {
  lastMessageStatus?: MessageStatus | null;
  lastMessageCreatedAt?: Date | null;
};

type GroupedChats = {
  today: ChatWithMessageStatus[];
  yesterday: ChatWithMessageStatus[];
  lastWeek: ChatWithMessageStatus[];
  lastMonth: ChatWithMessageStatus[];
  byMonth: Record<string, ChatWithMessageStatus[]>;
};

const groupChatsByDate = (chats: ChatWithMessageStatus[]): GroupedChats => {
  const now = new Date();
  const oneWeekAgo = subWeeks(now, 1);
  const oneMonthAgo = subMonths(now, 1);

  return chats.reduce(
    (groups, chat) => {
      const chatDate = new Date(chat.createdAt);

      if (isToday(chatDate)) {
        groups.today.push(chat);
      } else if (isYesterday(chatDate)) {
        groups.yesterday.push(chat);
      } else if (chatDate > oneWeekAgo) {
        groups.lastWeek.push(chat);
      } else if (chatDate > oneMonthAgo) {
        groups.lastMonth.push(chat);
      } else {
        // Group by "Month Year" (e.g., "January 2025")
        const monthYearKey = format(chatDate, 'MMMM yyyy');
        if (!groups.byMonth[monthYearKey]) {
          groups.byMonth[monthYearKey] = [];
        }
        groups.byMonth[monthYearKey].push(chat);
      }

      return groups;
    },
    {
      today: [],
      yesterday: [],
      lastWeek: [],
      lastMonth: [],
      byMonth: {},
    } as GroupedChats
  );
};

type ChatGroupProps = {
  chats: ChatWithMessageStatus[];
  title: string;
  activeChatId: string | string[] | null | undefined;
  streamingChatId: string | null;
  setOpenMobile: (open: boolean) => void;
};

const ChatGroup = memo(
  ({chats, title, activeChatId, streamingChatId, setOpenMobile}: ChatGroupProps) => {
    if (chats.length === 0) {
        return null;
    }

    return (
      <div>
        <div className="px-2 py-1 text-sidebar-foreground/70 text-xs font-medium">
          {title}
        </div>
        {chats.map((chat) => {
          const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
          const isMessageRecent = chat.lastMessageCreatedAt
            ? (Date.now() - new Date(chat.lastMessageCreatedAt).getTime()) < TIMEOUT_MS
            : false;
          const isLoadingFromStatus = isMessageRecent && (
            chat.lastMessageStatus === "pending" ||
            chat.lastMessageStatus === "streaming"
          );

          return (
            <ChatItem
              chat={chat}
              isActive={chat.id === activeChatId}
              isStreaming={chat.id === streamingChatId || isLoadingFromStatus}
              key={chat.id}
              setOpenMobile={setOpenMobile}
            />
          );
        })}
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.chats === nextProps.chats &&
    prevProps.title === nextProps.title &&
    prevProps.activeChatId === nextProps.activeChatId &&
    prevProps.streamingChatId === nextProps.streamingChatId
);

ChatGroup.displayName = "ChatGroup";

export function SidebarHistory({user}: { user: ClientSessionData["user"] | undefined }) {
  const {setOpenMobile} = useSidebar();
  const {id} = useParams();
  const streamingChatId = useStreamingChatId();
  const {t} = useTranslation();

  // Use streamingChatId as fallback when on root page (id is undefined)
  const activeChatId = id || streamingChatId;

  const {
    paginatedChatHistories,
    isLoading,
    hasMore,
    hasEmptyChatHistory,
    loadMore,
  } = useChatHistory(!!user);

  // Memoize the grouped chats calculation to avoid re-running on every render
  // IMPORTANT: This hook must be called before any conditional returns to comply with React's Rules of Hooks
  const groupedChats = useMemo(() => {
    if (!paginatedChatHistories) return null;

    const chatsFromHistory = paginatedChatHistories.flatMap(
      (paginatedChatHistory) => paginatedChatHistory.chats
    );

    return groupChatsByDate(chatsFromHistory);
  }, [paginatedChatHistories]);

  if (!user) {
    return (
      <SidebarGroup>
        <SidebarGroupContent/>
      </SidebarGroup>
    );
  }

  if (isLoading) {
    return (
      <SidebarGroup>
        <div className="px-2 py-1 text-sidebar-foreground/70 text-xs font-medium">
          {t(LocalizationKeys.SIDEBAR_TODAY)}
        </div>
        <SidebarGroupContent>
          <div className="flex flex-col">
            {[44, 32, 28, 64, 52].map((item) => (
              <div
                className="flex h-8 items-center gap-2 rounded-md px-2"
                key={item}
              >
                <div
                  className="h-4 max-w-(--skeleton-width) flex-1 rounded-md bg-sidebar-accent-foreground/10"
                  style={
                    {
                      "--skeleton-width": `${item}%`,
                    } as React.CSSProperties
                  }
                />
              </div>
            ))}
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  if (hasEmptyChatHistory) {
    return (
      <SidebarGroup>
        <div className="px-2 py-2 text-sidebar-foreground font-semibold text-sm flex items-center gap-2">
          <MessageIcon size={16}/>
          Chat History
        </div>
        <SidebarGroupContent>
          <div
            className="flex w-full flex-col items-center justify-center gap-3 px-4 py-8 text-sm text-muted-foreground text-center">
            <div className="opacity-30">
              <MessageIcon size={32}/>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-medium">No conversations yet</p>
              <p className="text-xs">
                Start a new chat to ask questions about patient care, medications, or treatment guidance. Your
                conversation history will appear here.
              </p>
            </div>
            {t(LocalizationKeys.SIDEBAR_EMPTY_STATE)}
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  return (
    <SidebarGroup>
      <div className="px-2 py-2 text-sidebar-foreground font-semibold text-sm flex items-center gap-2">
        <MessageIcon size={16}/>
        {t(LocalizationKeys.CHAT_HISTORY)}
      </div>
      <SidebarGroupContent>
        <SidebarMenu>
          {
            groupedChats && (
              <div className="flex flex-col gap-6">
                <ChatGroup
                  chats={groupedChats.today}
                  title={t(LocalizationKeys.SIDEBAR_TODAY)}
                  activeChatId={activeChatId}
                  streamingChatId={streamingChatId}
                  setOpenMobile={setOpenMobile}
                />
                <ChatGroup
                  chats={groupedChats.yesterday}
                  title={t(LocalizationKeys.SIDEBAR_YESTERDAY)}
                  activeChatId={activeChatId}
                  streamingChatId={streamingChatId}
                  setOpenMobile={setOpenMobile}
                />
                <ChatGroup
                  chats={groupedChats.lastWeek}
                  title={t(LocalizationKeys.SIDEBAR_LAST_7_DAYS)}
                  activeChatId={activeChatId}
                  streamingChatId={streamingChatId}
                  setOpenMobile={setOpenMobile}
                />
                <ChatGroup
                  chats={groupedChats.lastMonth}
                  title={t(LocalizationKeys.SIDEBAR_LAST_30_DAYS)}
                  activeChatId={activeChatId}
                  streamingChatId={streamingChatId}
                  setOpenMobile={setOpenMobile}
                />
                {Object.entries(groupedChats.byMonth).map(([monthYear, chatsInMonth]) => (
                  <ChatGroup
                    key={monthYear}
                    chats={chatsInMonth}
                    title={t(LocalizationKeys.SIDEBAR_OLDER_THAN_30_DAYS)}
                    activeChatId={activeChatId}
                    streamingChatId={streamingChatId}
                    setOpenMobile={setOpenMobile}
                  />
                ))}
              </div>
            )
          }
        </SidebarMenu>

        {hasMore && (
          <div className="mt-4 px-2">
            <button
              onClick={loadMore}
              disabled={isLoading}
              className="w-full rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading && (
                <div className="animate-spin">
                  <LoaderIcon size={14}/>
                </div>
              )}
              {isLoading ? 'Loading...' : 'See More'}
            </button>
          </div>
        )}

        {!hasMore && paginatedChatHistories && paginatedChatHistories.length > 0 && (
          <div className="mt-6 mb-4 flex flex-col items-center gap-2 px-4 py-3 text-center">
            <div className="text-sm font-medium text-sidebar-foreground/70">
              That's all your conversations
            </div>
            <div className="text-xs text-muted-foreground">
              You've reached the end of your chat history
            </div>
          </div>
        )}
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
